/**
 * ErgoRest cart logic, built on top of Horizon's native cart drawer.
 * - Prices always come from Shopify (automatic discounts), never computed here.
 * - One request per action: /cart/update.js + bundled section rendering.
 * - Auto-adds the free mask + shipping protection with the pillow in the SAME request.
 * - The mask + protection add to the PRICE but never count as items (badge = pillows only).
 * - Speed: drawer opens + badge updates INSTANTLY on click; content fills in when Shopify responds.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const PILLOW = 8747307925677;
const MASK = 15182273937581;
const MASK_DEFAULT = 67045818368173;
const SP = 15182290190509;
const SPV = 67045900812461;
const HOLD_MS = 10 * 60 * 1000;
const K_EXP = 'ergo:exp';
const K_N = 'ergo:n';
const K_SPOFF = 'ergo:spoff';

const store = {
  get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
  set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} },
  del(k) { try { sessionStorage.removeItem(k); } catch (e) {} },
};

const blank = () => ({ pq: 0, pv: 0, pillows: {}, masks: {}, sp: 0 });

function readState() {
  const el = document.querySelector('[data-ergo-state]');
  if (!el) return blank();
  try { return Object.assign(blank(), JSON.parse(el.textContent)); } catch (e) { return blank(); }
}

function stateFromItems(items) {
  const s = blank();
  for (const it of items || []) {
    if (it.product_id === PILLOW) {
      s.pq += it.quantity;
      s.pillows[it.variant_id] = (s.pillows[it.variant_id] || 0) + it.quantity;
      if (!s.pv) s.pv = it.variant_id;
    } else if (it.product_id === MASK) {
      s.masks[it.variant_id] = (s.masks[it.variant_id] || 0) + it.quantity;
    } else if (it.product_id === SP) {
      s.sp += it.quantity;
    }
  }
  return s;
}

/* ---------- loading overlay (injected once, no theme edits needed) ---------- */
(function injectLoadingStyles() {
  if (document.getElementById('ergo-loading-css')) return;
  const st = document.createElement('style');
  st.id = 'ergo-loading-css';
  st.textContent =
    'theme-drawer[ergo-loading] dialog{position:fixed}' +
    'theme-drawer[ergo-loading] dialog::after{content:"";position:absolute;inset:0;z-index:20;background:rgb(255 255 255/.72);backdrop-filter:blur(1px)}' +
    'theme-drawer[ergo-loading] dialog::before{content:"";position:absolute;top:50%;left:50%;width:28px;height:28px;margin:-14px 0 0 -14px;z-index:21;border:2.5px solid #000;border-right-color:transparent;border-radius:50%;animation:ergo-spin .6s linear infinite}' +
    '@keyframes ergo-spin{to{transform:rotate(360deg)}}';
  document.head.appendChild(st);
})();

const drawerEl = () => document.querySelector('theme-drawer#cart-drawer');
function openDrawer() {
  const d = drawerEl();
  if (d && typeof d.open === 'function') {
    if (!d.isOpen) d.open();
    return true;
  }
  return false;
}
function setLoading(on) {
  const d = drawerEl();
  if (d) d.toggleAttribute('ergo-loading', on);
}

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => ids.add(el.dataset.sectionId));
  if (!ids.size) ids.add('cart-drawer-section');
  return Array.from(ids).join(',');
}

/* runs right after the native components finish their re-render (next frame, not a fixed 60ms wait) */
const afterRender = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

/* ---------- header cart badge = pillows only ---------- */
function setBubble(n) {
  document.querySelectorAll('cart-icon').forEach((ic) => {
    const count = ic.querySelector('.cart-bubble__text-count');
    const bubble = ic.querySelector('.cart-bubble');
    if (count) {
      count.textContent = n > 0 && n < 100 ? String(n) : '';
      count.classList.toggle('hidden', n === 0);
    }
    if (bubble) bubble.classList.toggle('visually-hidden', n === 0);
    ic.classList.toggle('header-actions__cart-icon--has-cart', n > 0);
  });
  store.set('cart-count', JSON.stringify({ value: String(n), timestamp: Date.now() }));
}

let queue = Promise.resolve();
function mutate(url, body, action, lines) {
  const run = async () => {
    const deferred = CartLinesUpdateEvent.createPromise();
    (drawerEl() || document).dispatchEvent(
      new CartLinesUpdateEvent({ action, context: 'cart', lines: lines || [], promise: deferred.promise })
    );
    try {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({}, body, { sections: sectionIds(), sections_url: window.location.pathname })),
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'Cart update failed');
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(json),
        detail: { sections: json.sections, items: json.items, itemCount: json.item_count, source: 'ergo', didError: false },
      });
      const s = stateFromItems(json.items);
      afterRender(() => syncUI(s));
      return json;
    } catch (err) {
      deferred.reject(err);
      throw err;
    }
  };
  const p = queue.then(run, run);
  queue = p.catch(() => {});
  return p;
}

/* ---------- cart hold timer ---------- */
function bumpHold(n) {
  const prevN = Number(store.get(K_N)) || 0;
  if (!Number(store.get(K_EXP)) || n > prevN) store.set(K_EXP, String(Date.now() + HOLD_MS));
  store.set(K_N, String(n));
}
function clearHold() { store.del(K_EXP); store.del(K_N); }

let expiring = false;
function expire() {
  if (expiring) return;
  expiring = true;
  clearHold();
  mutate('/cart/clear.js', {}, 'remove', []).catch(() => {}).finally(() => { expiring = false; });
}

function tick(s) {
  s = s || readState();
  const el = document.querySelector('[data-ergo-timer]');
  if (!s.pq) {
    clearHold();
    if (el) el.hidden = true;
    return;
  }
  let exp = Number(store.get(K_EXP)) || 0;
  if (!exp) { bumpHold(s.pq); exp = Number(store.get(K_EXP)); }
  const rem = exp - Date.now();
  if (rem <= 0) {
    if (el) el.hidden = true;
    expire();
    return;
  }
  if (!el) return;
  const sec = Math.ceil(rem / 1000);
  const mm = Math.floor(sec / 60);
  const ss = sec % 60;
  el.innerHTML = '<span class="ergo-ic">&#9201;</span><span>Your cart is reserved for <b>' + mm + ':' + (ss < 10 ? '0' : '') + ss + '</b></span>';
  el.hidden = false;
  el.classList.toggle('is-low', sec <= 60);
}

function syncUI(s) {
  s = s || readState();
  tick(s);
  setBubble(s.pq);
  const c = document.querySelector('[data-ergo-sp]');
  if (c) c.checked = s.sp > 0;
}

/* ---------- keep gift + protection correct ---------- */
function plan(s) {
  const u = {};
  const spOff = store.get(K_SPOFF) === '1';
  const maskIds = Object.keys(s.masks || {});
  if (s.pq > 0) {
    if (!maskIds.length) {
      u[MASK_DEFAULT] = 1;
    } else {
      maskIds.forEach((id, i) => {
        const want = i === 0 ? 1 : 0;
        if (Number(s.masks[id]) !== want) u[id] = want;
      });
    }
    if (spOff) { if (s.sp) u[SPV] = 0; }
    else if (s.sp !== 1) u[SPV] = 1;
  } else {
    maskIds.forEach((id) => { u[id] = 0; });
    if (s.sp) u[SPV] = 0;
  }
  return u;
}

function reconcile(s) {
  const u = plan(s);
  if (!Object.keys(u).length) return Promise.resolve(null);
  return mutate('/cart/update.js', { updates: u }, 'update', []).catch(() => null);
}

/* ---------- public: add pillows (PDP + upsell) ---------- */
async function add(variantId, qty) {
  variantId = Number(variantId);
  qty = Math.max(1, Number(qty) || 1);
  const s = readState();
  const updates = {};
  updates[variantId] = (Number(s.pillows[variantId]) || 0) + qty;
  if (!Object.keys(s.masks || {}).length) updates[MASK_DEFAULT] = 1;
  if (!s.sp && store.get(K_SPOFF) !== '1') updates[SPV] = 1;

  // Instant feedback: badge + drawer react on the click, not after the network round trip.
  setBubble(s.pq + qty);
  const opened = openDrawer();
  setLoading(true);
  bumpHold(s.pq + qty);

  try {
    const json = await mutate('/cart/update.js', { updates }, 'add', [{ merchandiseId: String(variantId), quantity: qty }]);
    if (!opened) window.location.href = '/cart';
    return json;
  } catch (err) {
    setBubble(s.pq);
    throw err;
  } finally {
    afterRender(() => setLoading(false));
  }
}

/* ---------- any other cart change (native qty +/-, remove, quick add) ---------- */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (!event.promise) return;
  event.promise
    .then((res) => {
      const detail = res && res.detail;
      if (!detail || detail.source === 'ergo' || detail.didError) return;
      const fromItems = detail.items ? stateFromItems(detail.items) : null;
      afterRender(() => {
        const s = fromItems || readState();
        if (s.pq > (Number(store.get(K_N)) || 0)) bumpHold(s.pq);
        else if (s.pq) store.set(K_N, String(s.pq));
        reconcile(s);
        syncUI(s);
      });
    })
    .catch(() => {});
});

/* ---------- drawer controls ---------- */
document.addEventListener('click', (e) => {
  const t = e.target && e.target.closest ? e.target.closest('[data-ergo-add],[data-ergo-swap]') : null;
  if (!t) return;
  e.preventDefault();
  if (t.hasAttribute('data-ergo-add')) {
    t.classList.add('ergo-busy');
    add(t.getAttribute('data-ergo-add'), 1).catch(() => {}).finally(() => t.classList.remove('ergo-busy'));
    return;
  }
  if (t.classList.contains('is-on')) return;
  const u = {};
  u[t.getAttribute('data-ergo-from')] = 0;
  u[t.getAttribute('data-ergo-swap')] = 1;
  const wrap = t.closest('.ergo-swap');
  if (wrap) wrap.classList.add('ergo-busy');
  mutate('/cart/update.js', { updates: u }, 'update', []).catch(() => {});
});

document.addEventListener('change', (e) => {
  const c = e.target && e.target.closest ? e.target.closest('[data-ergo-sp]') : null;
  if (!c) return;
  const on = c.checked;
  store.set(K_SPOFF, on ? '0' : '1');
  const u = {};
  u[SPV] = on ? 1 : 0;
  mutate('/cart/update.js', { updates: u }, 'update', []).catch(() => {});
});

window.ErgoCart = { add, open: openDrawer, mutate };
document.dispatchEvent(new CustomEvent('ergo:ready'));

(function init() {
  const s = readState();
  reconcile(s);
  setBubble(s.pq);
  tick(s);
  setInterval(() => tick(), 1000);
})();
